import ReactDOM from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import App from 'App';
import * as ApplicationApi from 'api/applicationApi';
import BlockUI from 'components/BlockUI';
import * as ServiceTypeConstants from 'data/serviceTypeConstant';
import * as HomePageErrorCodes from 'pages/Home/data/errorCode';
import { pageNames, pagePaths, pageRoutes } from 'utils/routes';
import * as RoutesUtils from 'utils/routesUtils';
import * as SessionStorage from 'utils/sessionStorage';
import * as ThemeUtils from 'utils/themeUtils';

ThemeUtils.updateTheme();

const rootElement = ReactDOM.createRoot(document.getElementById('root'));
const blockUIElement = ReactDOM.createRoot(document.getElementById('blockui'));
const showLoading = () => blockUIElement.render(<BlockUI blocking />);
const hideLoading = () => blockUIElement.unmount();

const urlParams = new URLSearchParams(window.location.search);
const urlAccountBookId = urlParams.get('accountBookId');
const urlQRCode = urlParams.get('qrCode');

const onQRCodeScanned = async () => {
    showLoading();

    SessionStorage.removeSessionData(); // Remove data from session storage
    SessionStorage.setAccountBookId(urlAccountBookId);

    try {
        const result = await ApplicationApi.validateQRCode(urlQRCode);
        hideLoading();

        if (result.isQRCodeInvalid) {
            RoutesUtils.redirectToPageByPath(pagePaths.QRCodeInvalid);
        } else {
            SessionStorage.setQRCode(result.qrCodeId);
            SessionStorage.setTableNo(result.tableNo);
            SessionStorage.setServiceType(result.serviceType);
            SessionStorage.setIsQRCodeStatic(result.isQRCodeStatic);
            SessionStorage.setIsPreview(result.isPreview);

            const options = result.isQRCodeExpired ? { errorCode: HomePageErrorCodes.QRCodeExpired } : null;

            RoutesUtils.redirectToPageByName(pageNames.Home, options);
        }
    } catch {
        hideLoading();
        RoutesUtils.redirectToPageByPath(pagePaths.QRCodeInvalid);
    }
};

const onAppInitialized = async () => {
    const parseIntValue = (value) => {
        const intValue = parseInt(value);
        return isNaN(intValue) ? null : intValue;
    };

    const accountBookId = parseIntValue(SessionStorage.getAccountBookId());
    const tableSessionId = parseIntValue(SessionStorage.getTableSessionId());
    const serviceType = SessionStorage.getServiceType();
    const isPreview = SessionStorage.getIsPreview();

    let qrCode = SessionStorage.getQRCode();
    let tableNo = SessionStorage.getTableNo();

    // Check QR Code is valid
    const isDineIn = serviceType === ServiceTypeConstants.DineIn && tableNo;
    const isTakeAway = serviceType === ServiceTypeConstants.TakeAway;

    const isValidQRCode = accountBookId && qrCode && (isDineIn || isTakeAway || isPreview);
    if (!isValidQRCode) {
        RoutesUtils.redirectToPageByPath(pagePaths.QRCodeInvalid);
        return;
    }

    // Update qrCode & tableNo if table has changed since last session
    if (tableSessionId && tableNo) {
        showLoading();

        try {
            const result = await ApplicationApi.hasChangeTable(qrCode, tableSessionId);
            hideLoading();

            if (result.hasChangeTable) {
                SessionStorage.setQRCode(result.qrCodeId);
                SessionStorage.setTableNo(result.tableNo);
                qrCode = result.qrCodeId;
                tableNo = result.tableNo;
            }
        } catch {
            hideLoading();
            RoutesUtils.redirectToPageByPath(pagePaths.QRCodeInvalid);
            return;
        }
    }

    // Ensure user is on the Home Page or a valid redirect path
    const pathName = window.location.pathname;
    if (!pathName.startsWith(pagePaths.Home) && !pathName.startsWith('/redirect')) {
        RoutesUtils.redirectToPageByName(pageNames.Home);
    }

    const browserRouter = createBrowserRouter([
        {
            path: '/',
            element: <App />,
            children: pageRoutes.map((route) => ({
                path: route.path,
                element: <route.component />
            }))
        }
    ]);

    rootElement.render(<RouterProvider router={browserRouter} />);
};

if (urlAccountBookId && urlQRCode) {
    onQRCodeScanned();
} else {
    onAppInitialized();
}
